import { useState } from "react"

/**
 * Drag a list into a different order — with the mouse, or from the keyboard.
 *
 * ── Why the HTML5 API and not a library ─────────────────────────────────────
 *
 * The same reason the board gives: the interaction is one row into one gap in
 * one list, and a drag-and-drop dependency is a lot of bundle and API surface
 * for that. It also degrades honestly — every row still opens on click.
 *
 * ── One list per hook ───────────────────────────────────────────────────────
 *
 * The project page has a list of tasks with a list of steps inside each, and a
 * drag that wanders from a step into the task list must do nothing rather than
 * pull the step out of its task. Each list's hook only answers to a drag it
 * started itself, and stops the event there, so the outer list never sees a
 * drag that began in an inner one.
 *
 * ── The keyboard path ───────────────────────────────────────────────────────
 *
 * A drag is a pointer gesture; somebody on a keyboard needs the same result
 * another way. The handle is a real button, and Arrow Up / Arrow Down on it
 * move the row one place, saving each move the same way a drop does.
 *
 * @param {Array} items The list, in the order shown.
 * @param {object} options
 * @param {(ids: Array) => void} options.onReorder Called with every id, in the new order.
 * @param {boolean} [options.enabled]
 */
export function useDragReorder(items, { onReorder, enabled = true, getId = (item) => item.id }) {
  const [draggingId, setDraggingId] = useState(null)
  const [target, setTarget] = useState(null) // { id, after }

  const ids = items.map(getId)

  const commit = (movingId, beforeIndex) => {
    const without = ids.filter((id) => id !== movingId)
    const at = Math.max(0, Math.min(beforeIndex, without.length))
    const next = [...without.slice(0, at), movingId, ...without.slice(at)]
    if (next.every((id, index) => id === ids[index])) return
    onReorder(next)
  }

  const end = () => {
    setDraggingId(null)
    setTarget(null)
  }

  /** Spread on the row that can be picked up. */
  const rowProps = (item) => {
    const id = getId(item)
    if (!enabled) return {}
    return {
      draggable: true,
      onDragStart: (event) => {
        event.stopPropagation()
        event.dataTransfer.effectAllowed = "move"
        // Some browsers start no drag without data on the transfer.
        event.dataTransfer.setData("text/plain", String(id))
        setDraggingId(id)
      },
      onDragEnd: (event) => {
        event.stopPropagation()
        end()
      },
      onDragOver: (event) => {
        if (draggingId == null) return // a drag from some other list
        event.preventDefault()
        event.stopPropagation()
        const box = event.currentTarget.getBoundingClientRect()
        const after = event.clientY > box.top + box.height / 2
        if (target?.id !== id || target?.after !== after) setTarget({ id, after })
      },
      onDrop: (event) => {
        if (draggingId == null) return
        event.preventDefault()
        event.stopPropagation()
        const without = ids.filter((other) => other !== draggingId)
        const index = without.indexOf(id)
        if (index !== -1) commit(draggingId, target?.id === id && target.after ? index + 1 : index)
        end()
      },
    }
  }

  /** Spread on the handle button: Arrow Up / Down move the row one place. */
  const handleProps = (item) => {
    const id = getId(item)
    return {
      disabled: !enabled,
      onKeyDown: (event) => {
        if (event.key !== "ArrowUp" && event.key !== "ArrowDown") return
        event.preventDefault()
        event.stopPropagation()
        const index = ids.indexOf(id)
        const next = event.key === "ArrowUp" ? index - 1 : index + 1
        if (next < 0 || next >= ids.length) return
        commit(id, next)
      },
      onClick: (event) => event.stopPropagation(),
    }
  }

  /** sx for a row: the one being dragged fades, a line marks where it will land. */
  const rowSx = (item) => {
    const id = getId(item)
    return {
      opacity: draggingId === id ? 0.4 : 1,
      boxShadow: (theme) =>
        target?.id === id && draggingId !== id
          ? `inset 0 ${target.after ? "-2px" : "2px"} 0 0 ${theme.palette.primary.main}`
          : "none",
    }
  }

  return { rowProps, handleProps, rowSx, dragging: draggingId != null }
}

/** `items` sorted by `orderIds`, with anything not in it kept at the end in its own order. */
export function applyOrder(items, orderIds, getId = (item) => item.id) {
  if (!orderIds) return items
  const position = new Map(orderIds.map((id, index) => [id, index]))
  return [...items].sort((a, b) => {
    const pa = position.has(getId(a)) ? position.get(getId(a)) : Number.MAX_SAFE_INTEGER
    const pb = position.has(getId(b)) ? position.get(getId(b)) : Number.MAX_SAFE_INTEGER
    return pa - pb
  })
}
